/**
 * Lightweight analytics shim — talks to GA4 (gtag) and Meta Pixel (fbq) if loaded.
 * Always pushes to window.dataLayer too, so events are captured even if the
 * gtag function reference hasn't booted yet at call time.
 *
 * Mirrors the same events to the TagadaPay External Page Tracker when
 * configured — see `lib/tagadaTracker.js`.
 */
import { trackTagadaEvent } from './tagadaTracker';

const gtag = (...args) => {
  if (typeof window === 'undefined') return;
  // Always push to dataLayer — GA4 tag boot will replay it.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
  if (typeof window.gtag === 'function') {
    try { window.gtag(...args); } catch { /* ignore */ }
  }
};

const fbq = (...args) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq(...args);
  }
};

export const trackAddToCart = ({ id, name, price, quantity = 1, variant }) => {
  gtag('event', 'add_to_cart', {
    currency: 'USD',
    value: Number(price) * Number(quantity || 1),
    items: [{ item_id: id, item_name: name, item_variant: variant, price, quantity }],
  });
  fbq('track', 'AddToCart', {
    content_ids: [id],
    content_name: name,
    content_type: 'product',
    value: Number(price) * Number(quantity || 1),
    currency: 'USD',
  });
  trackTagadaEvent('add_to_cart', {
    productId: id,
    productName: name,
    variant,
    quantity,
    price: Number(price),
    value: Number(price) * Number(quantity || 1),
    currency: 'USD',
  });
};

export const trackBeginCheckout = ({ value, items }) => {
  gtag('event', 'begin_checkout', {
    currency: 'USD',
    value: Number(value),
    items: (items || []).map((i) => ({
      item_id: i.product_id,
      item_name: i.name,
      item_variant: i.variant,
      price: i.price,
      quantity: i.quantity,
    })),
  });
  fbq('track', 'InitiateCheckout', {
    content_ids: (items || []).map((i) => i.product_id),
    contents: (items || []).map((i) => ({ id: i.product_id, quantity: i.quantity })),
    num_items: (items || []).reduce((s, i) => s + Number(i.quantity || 0), 0),
    value: Number(value),
    currency: 'USD',
  });
  trackTagadaEvent('begin_checkout', {
    value: Number(value),
    currency: 'USD',
    numItems: (items || []).reduce((s, i) => s + Number(i.quantity || 0), 0),
    items: (items || []).map((i) => ({
      productId: i.product_id,
      name: i.name,
      variant: i.variant,
      price: i.price,
      quantity: i.quantity,
    })),
  });
};

export const trackPurchase = ({ orderId, value, items }) => {
  gtag('event', 'purchase', {
    transaction_id: orderId,
    currency: 'USD',
    value: Number(value),
    items: (items || []).map((i) => ({
      item_id: i.product_id,
      item_name: i.name,
      item_variant: i.variant,
      price: i.price,
      quantity: i.quantity,
    })),
  });
  fbq('track', 'Purchase', {
    content_ids: (items || []).map((i) => i.product_id),
    contents: (items || []).map((i) => ({ id: i.product_id, quantity: i.quantity })),
    value: Number(value),
    currency: 'USD',
  });
  trackTagadaEvent('purchase', {
    orderId,
    value: Number(value),
    currency: 'USD',
    items: (items || []).map((i) => ({
      productId: i.product_id,
      name: i.name,
      variant: i.variant,
      price: i.price,
      quantity: i.quantity,
    })),
  });
};

export const trackPageView = (path) => {
  gtag('event', 'page_view', { page_path: path });
  fbq('track', 'PageView');
  trackTagadaEvent('page_view', { path });
};

export const trackViewItem = ({ id, name, price }) => {
  gtag('event', 'view_item', {
    currency: 'USD',
    value: Number(price || 0),
    items: [{ item_id: id, item_name: name, price }],
  });
  fbq('track', 'ViewContent', {
    content_ids: [id],
    content_name: name,
    content_type: 'product',
    value: Number(price || 0),
    currency: 'USD',
  });
  trackTagadaEvent('view_item', {
    productId: id,
    productName: name,
    price: Number(price || 0),
    currency: 'USD',
  });
};
